import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Building2, Phone, Globe, MapPin, Clock, Users, 
  Plus, Trash2, Save, X, Image, Upload, Loader2
} from 'lucide-react';
import Navigation from '../components/Navigation';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { formatPhoneNumber, formatPhoneInput } from '../utils/phoneFormat';

import { API, API_BASE } from '../config/api';
const BACKEND_URL = API_BASE;

function FirmSettings({ user }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [coverImage, setCoverImage] = useState(null);
  const [uploadingCover, setUploadingCover] = useState(false);
  const coverInputRef = useRef(null);
  const [firmInfo, setFirmInfo] = useState({
    firm_name: '',
    phone: '',
    fax: '',
    website: '',
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    zip_code: '',
    hours_english: '',
    hours_note_english: '',
    hours_spanish: '',
    hours_note_spanish: '',
    staff_directory: []
  });

  useEffect(() => {
    fetchFirmInfo();
    fetchLoginCover();
  }, []);

  const fetchFirmInfo = async () => {
    try {
      const response = await fetch(`${API}/firm-info`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setFirmInfo(data);
      }
    } catch (error) {
      toast.error('Failed to load firm information');
    } finally {
      setLoading(false);
    }
  };

  const fetchLoginCover = async () => {
    try {
      const response = await fetch(`${API}/settings/login-cover`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        if (data.image_url) {
          setCoverImage(`${BACKEND_URL}${data.image_url}?t=${Date.now()}`);
        }
      }
    } catch (error) {
      console.debug('No custom login cover set');
    }
  };

  const handleCoverUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    // Validate file size (max 50MB)
    if (file.size > 50 * 1024 * 1024) {
      toast.error('Image must be less than 50MB');
      return;
    }

    setUploadingCover(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${API}/admin/settings/login-cover`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        setCoverImage(`${BACKEND_URL}${data.image_url}?t=${Date.now()}`);
        toast.success('Login cover photo updated!');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to upload cover photo');
      }
    } catch (error) {
      toast.error('Failed to upload cover photo');
    } finally {
      setUploadingCover(false);
      if (coverInputRef.current) {
        coverInputRef.current.value = '';
      }
    }
  };

  const handleRemoveCover = async () => {
    if (!window.confirm('Remove the custom login cover photo? The default image will be used.')) {
      return;
    }

    setUploadingCover(true);
    try {
      const response = await fetch(`${API}/admin/settings/login-cover`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        setCoverImage(null);
        toast.success('Login cover photo removed');
      } else {
        toast.error('Failed to remove cover photo');
      }
    } catch (error) {
      toast.error('Failed to remove cover photo');
    } finally {
      setUploadingCover(false);
    }
  };

  const handleLogout = async () => {
    try {
      await fetch(`${API}/auth/logout`, { method: 'POST', credentials: 'include' });
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API}/firm-info`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(firmInfo)
      });

      if (response.ok) {
        toast.success('Firm information updated successfully');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to update firm information');
      }
    } catch (error) {
      toast.error('Failed to update firm information');
    } finally {
      setSaving(false);
    }
  };

  const addStaffMember = () => {
    setFirmInfo({
      ...firmInfo,
      staff_directory: [
        ...firmInfo.staff_directory,
        { name: '', email: '', extension: '' }
      ]
    });
  };

  const removeStaffMember = (index) => {
    const newStaff = [...firmInfo.staff_directory];
    newStaff.splice(index, 1);
    setFirmInfo({ ...firmInfo, staff_directory: newStaff });
  };

  const updateStaffMember = (index, field, value) => {
    const newStaff = [...firmInfo.staff_directory];
    newStaff[index] = { ...newStaff[index], [field]: value };
    setFirmInfo({ ...firmInfo, staff_directory: newStaff });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFCFE]">
        <Navigation user={user} onLogout={handleLogout} />
        <div className="flex items-center justify-center h-[calc(100vh-80px)]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#80298F]"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFCFE]">
      <Navigation user={user} onLogout={handleLogout} />
      
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/dashboard')}
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Firm Settings</h1>
              <p className="text-gray-600">Manage your firm's contact information</p>
            </div>
          </div>
          <Button 
            onClick={handleSave} 
            disabled={saving}
            className="bg-[#80298F] hover:bg-[#9b3ba8]"
          >
            <Save className="h-4 w-4 mr-2" />
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>

        <div className="space-y-6">
          {/* Login Cover Photo */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Image className="h-5 w-5 text-[#80298F]" />
                Login Page Cover Photo
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-gray-600 mb-4">
                This image appears on the left side of the login page. Recommended size: 1920x1080 pixels.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 items-start">
                {/* Preview */}
                <div className="w-full sm:w-64 h-40 bg-gray-100 rounded-lg overflow-hidden border-2 border-dashed border-gray-300 relative">
                  {coverImage ? (
                    <img 
                      src={coverImage} 
                      alt="Login cover preview" 
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="flex flex-col items-center justify-center h-full text-gray-400">
                      <Image className="h-8 w-8 mb-2" />
                      <span className="text-xs">Default image in use</span>
                    </div>
                  )}
                  {uploadingCover && (
                    <div className="absolute inset-0 bg-white/80 flex items-center justify-center">
                      <Loader2 className="h-8 w-8 animate-spin text-[#80298F]" />
                    </div>
                  )}
                </div>
                
                {/* Actions */}
                <div className="flex flex-col gap-2">
                  <input
                    ref={coverInputRef}
                    type="file"
                    accept="image/jpeg,image/jpg,image/png,image/webp"
                    onChange={handleCoverUpload}
                    className="hidden"
                    id="cover-upload"
                  />
                  <Button
                    variant="outline"
                    onClick={() => coverInputRef.current?.click()}
                    disabled={uploadingCover}
                    className="w-full sm:w-auto"
                  >
                    <Upload className="h-4 w-4 mr-2" />
                    {coverImage ? 'Change Photo' : 'Upload Photo'}
                  </Button>
                  {coverImage && (
                    <Button
                      variant="outline"
                      onClick={handleRemoveCover}
                      disabled={uploadingCover}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Remove
                    </Button>
                  )}
                  <p className="text-xs text-gray-500 mt-1">
                    JPG, PNG, or WebP. Max 50MB.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Basic Information */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="h-5 w-5 text-[#80298F]" />
                Basic Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Firm Name *</Label>
                  <Input
                    value={firmInfo.firm_name}
                    onChange={(e) => setFirmInfo({ ...firmInfo, firm_name: e.target.value })}
                    placeholder="Law Firm Name"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Website</Label>
                  <div className="flex items-center gap-2">
                    <Globe className="h-4 w-4 text-gray-400" />
                    <Input
                      value={firmInfo.website}
                      onChange={(e) => setFirmInfo({ ...firmInfo, website: e.target.value })}
                      placeholder="https://example.com"
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Contact Information */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Phone className="h-5 w-5 text-[#80298F]" />
                Contact Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Phone Number *</Label>
                  <Input
                    value={formatPhoneNumber(firmInfo.phone)}
                    onChange={(e) => setFirmInfo({ ...firmInfo, phone: formatPhoneInput(e.target.value) })}
                    placeholder="(310) 567-7777"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Fax Number</Label>
                  <Input
                    value={formatPhoneNumber(firmInfo.fax)}
                    onChange={(e) => setFirmInfo({ ...firmInfo, fax: formatPhoneInput(e.target.value) })}
                    placeholder="(310) 567-7778"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Address */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MapPin className="h-5 w-5 text-[#80298F]" />
                Address
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Address Line 1 *</Label>
                <Input
                  value={firmInfo.address_line1}
                  onChange={(e) => setFirmInfo({ ...firmInfo, address_line1: e.target.value })}
                  placeholder="123 Main Street, Suite 100"
                />
              </div>
              <div className="space-y-2">
                <Label>Address Line 2</Label>
                <Input
                  value={firmInfo.address_line2}
                  onChange={(e) => setFirmInfo({ ...firmInfo, address_line2: e.target.value })}
                  placeholder="Building B, Floor 2"
                />
              </div>
              <div className="grid md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>City *</Label>
                  <Input
                    value={firmInfo.city}
                    onChange={(e) => setFirmInfo({ ...firmInfo, city: e.target.value })}
                    placeholder="Los Angeles"
                  />
                </div>
                <div className="space-y-2">
                  <Label>State *</Label>
                  <Input
                    value={firmInfo.state}
                    onChange={(e) => setFirmInfo({ ...firmInfo, state: e.target.value })}
                    placeholder="CA"
                  />
                </div>
                <div className="space-y-2">
                  <Label>ZIP Code *</Label>
                  <Input
                    value={firmInfo.zip_code}
                    onChange={(e) => setFirmInfo({ ...firmInfo, zip_code: e.target.value })}
                    placeholder="90210"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Business Hours */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="h-5 w-5 text-[#80298F]" />
                Business Hours
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid md:grid-cols-2 gap-6">
                {/* English */}
                <div className="space-y-4">
                  <h4 className="font-medium text-gray-700">English</h4>
                  <div className="space-y-2">
                    <Label>Hours</Label>
                    <Input
                      value={firmInfo.hours_english}
                      onChange={(e) => setFirmInfo({ ...firmInfo, hours_english: e.target.value })}
                      placeholder="Mon-Fri: 9:00 a.m. to 5:00 p.m."
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Note</Label>
                    <Input
                      value={firmInfo.hours_note_english}
                      onChange={(e) => setFirmInfo({ ...firmInfo, hours_note_english: e.target.value })}
                      placeholder="By appointment only"
                    />
                  </div>
                </div>
                
                {/* Spanish */}
                <div className="space-y-4">
                  <h4 className="font-medium text-gray-700">Español</h4>
                  <div className="space-y-2">
                    <Label>Horario</Label>
                    <Input
                      value={firmInfo.hours_spanish}
                      onChange={(e) => setFirmInfo({ ...firmInfo, hours_spanish: e.target.value })}
                      placeholder="Lun-Vie: 9:00 a.m. a 5:00 p.m."
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Nota</Label>
                    <Input
                      value={firmInfo.hours_note_spanish}
                      onChange={(e) => setFirmInfo({ ...firmInfo, hours_note_spanish: e.target.value })}
                      placeholder="Solo con cita previa"
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Staff Directory */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Users className="h-5 w-5 text-[#80298F]" />
                  Staff Directory
                </CardTitle>
                <Button variant="outline" size="sm" onClick={addStaffMember}>
                  <Plus className="h-4 w-4 mr-1" />
                  Add Staff
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {firmInfo.staff_directory.length === 0 ? (
                <p className="text-center text-gray-500 py-4">No staff members added yet</p>
              ) : (
                <div className="space-y-4">
                  {firmInfo.staff_directory.map((staff, index) => (
                    <div key={index} className="flex items-start gap-4 p-4 bg-gray-50 rounded-lg">
                      <div className="flex-1 grid md:grid-cols-3 gap-4">
                        <div className="space-y-1">
                          <Label className="text-xs">Name</Label>
                          <Input
                            value={staff.name}
                            onChange={(e) => updateStaffMember(index, 'name', e.target.value)}
                            placeholder="John Doe"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Email</Label>
                          <Input
                            value={staff.email}
                            onChange={(e) => updateStaffMember(index, 'email', e.target.value)}
                            placeholder="john@example.com"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Extension</Label>
                          <Input
                            value={staff.extension}
                            onChange={(e) => updateStaffMember(index, 'extension', e.target.value)}
                            placeholder="100"
                          />
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50 mt-5"
                        onClick={() => removeStaffMember(index)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Save Button (Bottom) */}
          <div className="flex justify-end pt-4">
            <Button 
              onClick={handleSave} 
              disabled={saving}
              className="bg-[#80298F] hover:bg-[#9b3ba8]"
            >
              <Save className="h-4 w-4 mr-2" />
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </div>
      </main>
    </div>
  );
}

export default FirmSettings;
